import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useHouseholdSettings, useUpdateHouseholdSettings } from '../hooks/useHousehold';
import { useDeleteAccount } from '../hooks/useUserProfile';
import { Modal } from '../components/Modal';
import { Switch } from '../components/Switch';
import { HouseholdSwitcher } from '../components/HouseholdSwitcher';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import { useActiveMemberContext } from '../context/ActiveMemberContext';
import { clearWidget } from '../lib/widget';

export function HouseholdSettingsPage() {
  const navigate = useNavigate();
  const { householdId, households } = useSelectedHousehold();
  const currentHousehold = households?.find((h) => h.householdId === householdId);

  const { activeMemberId } = useActiveMemberContext();
  // Shared-device users can view settings but not change them.
  const readOnly = !!activeMemberId;

  const { data: settings, isLoading } = useHouseholdSettings(householdId);
  const updateSettings = useUpdateHouseholdSettings(householdId);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const deleteAccount = useDeleteAccount();

  const handleDeleteAccount = () => {
    deleteAccount.mutate(undefined, {
      onSuccess: async () => {
        await signOut();
        void clearWidget();
        navigate('/signin');
      },
    });
  };

  if (!householdId) {
    return (
      <div className="page">
        <div className="empty">Create or join a household first.</div>
      </div>
    );
  }

  if (isLoading || !settings) {
    return (
      <div className="page">
        <p className="loading-note">Loading settings…</p>
      </div>
    );
  }

  const toggle = (
    key: 'autoMarkOverdueMissed' | 'assignDuplicatesOfOverdue' | 'allowSkipping' | 'allowSharedSwitchByAll',
  ) => {
    void updateSettings.mutate({ [key]: !settings[key] });
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Settings</h1>
        <HouseholdSwitcher />
      </div>

      {readOnly && (
        <div className="card notice-card">
          <ion-icon name="eye-outline" />
          <span className="notice-text">
            You&apos;re acting as a shared-device user. Settings are view-only — switch back to
            yourself to change them.
          </span>
        </div>
      )}

      {/* Everything under this heading is scoped to the selected household. */}
      <h2 className="settings-scope">
        <ion-icon name="home" aria-hidden="true" />
        {currentHousehold?.name ?? 'Household'}
      </h2>
      <p className="settings-scope-sub">These settings apply to everyone in this household.</p>

      <div className="card">
        <p className="section-label">Pending tasks</p>

        <div className="item-row setting-row">
          <div className="setting-text">
            <strong>Allow skipping chores</strong>
            <div className="setting-desc">
              Members can skip a chore without penalty. Skipped chores will not award points but will still be scheduled again in the future.
            </div>
          </div>
          <Switch
            checked={settings.allowSkipping}
            onChange={() => toggle('allowSkipping')}
            disabled={updateSettings.isPending || readOnly}
            label="Allow skipping chores"
          />
        </div>
      </div>

      <div className="card">
        <p className="section-label">Overdue tasks</p>

        <div className="item-row setting-row">
          <div className="setting-text">
            <strong>Automatically mark overdue tasks as missed</strong>
            <div className="setting-desc">
              When enabled, any pending tasks will be marked as &ldquo;Missed&rdquo;
              before new assignments are created.
            </div>
          </div>
          <Switch
            checked={settings.autoMarkOverdueMissed}
            onChange={() => toggle('autoMarkOverdueMissed')}
            disabled={updateSettings.isPending || readOnly}
            label="Automatically mark overdue tasks as missed"
          />
        </div>

        {!settings.autoMarkOverdueMissed && (
          <div className="item-row setting-row" style={{ marginTop: '0.5rem' }}>
            <div className="setting-text">
              <strong>Assign duplicates of overdue tasks</strong>
              <div className="setting-desc">
                When enabled, you will keep getting new tasks even if the same chore is already overdue.
              </div>
            </div>
            <Switch
              checked={settings.assignDuplicatesOfOverdue}
              onChange={() => toggle('assignDuplicatesOfOverdue')}
              disabled={updateSettings.isPending || readOnly}
              label="Assign duplicates of overdue tasks"
            />
          </div>
        )}
      </div>


      <div className="card">
        <p className="section-label">Shared-device users</p>
        <div className="item-row setting-row">
          <div className="setting-text">
            <strong>Anyone can switch into shared users</strong>
            <div className="setting-desc">
              When on, any household member can switch into shared-device users from the dashboard.
              When off, only the member who created a shared user can switch into them.
            </div>
          </div>
          <Switch
            checked={settings.allowSharedSwitchByAll}
            onChange={() => toggle('allowSharedSwitchByAll')}
            disabled={updateSettings.isPending || readOnly}
            label="Anyone can switch into shared users"
          />
        </div>
      </div>

      {!readOnly && (
        <>
          <h2 className="settings-scope">
            <ion-icon name="person-circle-outline" aria-hidden="true" />
            Your account
          </h2>
          <p className="settings-scope-sub">Just you — applies across all your households.</p>

          <div className="card">
            <div className="item-row setting-row">
              <div className="setting-text">
                <strong>Delete account</strong>
                <div className="setting-desc">
                  Permanently deletes your account. This cannot be undone.
                </div>
              </div>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}

      <div className="page-footnote">
        {__NATIVE__ ? (
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.open('https://chores.stubie.co.uk/privacy-policy', '_blank'); }}
          >
            Privacy Policy
          </a>
        ) : (
          <Link to="/privacy-policy">Privacy Policy</Link>
        )}
      </div>

      {showDeleteModal && (
        <Modal title="Delete account" onClose={() => setShowDeleteModal(false)}>
          <p>Are you sure you want to delete your account? This will permanently remove your data and cannot be undone.</p>
          {deleteAccount.isError && (
            <div className="alert alert-error">Something went wrong. Please try again.</div>
          )}
          <div className="form-actions form-actions--end">
            <button className="btn btn-ghost" onClick={() => setShowDeleteModal(false)} disabled={deleteAccount.isPending}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDeleteAccount} disabled={deleteAccount.isPending}>
              {deleteAccount.isPending ? 'Deleting…' : 'Delete account'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
